import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/config/brand";
import { CalendarDays, Video, ShieldCheck, HeartHandshake, Users, GraduationCap, Sparkles } from "lucide-react";
import BrandStoryPanel from "@/components/BrandStoryPanel";

const VIBE_IMG = "https://images.unsplash.com/photo-1758876201548-ade1eff8b169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxwZWFjZWZ1bCUyMHRoZXJhcHklMjByb29tfGVufDB8fHx8MTc4NTM2MzkyNnww&ixlib=rb-4.1.0&q=85";
const HERO_IMG = "https://images.unsplash.com/photo-1528200575999-1853e416cf07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxjYWxtJTIwYWJzdHJhY3QlMjBuYXR1cmUlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NTM2MzkyNnww&ixlib=rb-4.1.0&q=85";

function LangBadges({ langs }) {
  if (!langs || langs.length === 0) return null;
  const map = { en: "EN", hi: "हिन्दी" };
  return (
    <div className="flex gap-1 flex-wrap">
      {langs.map(l => (
        <span key={l} className="chip text-[0.6rem]" style={{background:"rgba(230,178,58,0.12)", color:"var(--accent)"}}>{map[l] || l.toUpperCase()}</span>
      ))}
    </div>
  );
}

function formatExperience(months) {
  const m = Number(months);
  if (!m || m < 0) return null;
  if (m < 12) return `${m} month${m === 1 ? "" : "s"} experience`;
  const yrs = Math.floor(m / 12);
  const rem = m % 12;
  if (rem === 0) return `${yrs}+ yrs experience`;
  return `${yrs}y ${rem}m experience`;
}

function CounselorCard({ c, idx }) {
  const exp = formatExperience(c.experience_months);
  return (
    <div data-testid={`team-card-${c.id}`}
      className="card-soft p-6 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
             style={{background: "var(--bg-alt)"}}>
          {c.photo_url
            ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center font-serif-display text-2xl" style={{color:"var(--brand)"}}>{c.name.slice(0,1)}</div>}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-serif-display text-xl leading-tight truncate">{c.name}</h3>
          </div>
          <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>{c.title}</div>
          <div className="mt-1"><LangBadges langs={c.languages}/></div>
        </div>
      </div>
      <div className="text-sm" style={{color:"var(--brand)"}}>{c.specialty}</div>
      <p className="text-sm leading-relaxed" style={{color:"var(--text-secondary)"}}>{c.bio}</p>
      {exp && (
        <div className="text-xs uppercase tracking-widest" style={{color:"var(--text-secondary)"}}>
          {exp}
        </div>
      )}
      <Link to="/login" data-testid={`team-book-${c.id}`}
        className="btn-outline text-sm w-fit mt-auto">Book with {c.name.split(" ")[0]}</Link>
    </div>
  );
}

export default function Landing() {
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
    api.get("/counselors").then(r => setTeam(r.data)).catch(() => {});
  }, []);

  const followUp = services.find(s => s.id === "svc_followup");
  const yearlyWeeklyPrice = followUp ? followUp.price * 52 : 0;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8" data-testid="hero-copy">
            <div className="brand-lockup" tabIndex={0} data-testid="brand-lockup">
              <img src={BRAND.logoUrl} alt={`${BRAND.shortName} logo`} className="w-[4.5rem] h-[4.5rem] rounded-full object-cover shadow-sm" />
              <span className="font-serif-display text-lg tracking-[0.08em] uppercase" style={{ color: "var(--text-primary)" }}>
                {BRAND.shortName}
              </span>
              <BrandStoryPanel />
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight" style={{ color: "var(--text-primary)" }}>
              Independent counselors,<br />
              ready to listen to the<br />
              <em className="italic" style={{ color: "var(--brand)" }}>voices in your mind</em>.
            </h1>
            <p className="max-w-xl text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              A private tele-counseling <strong>platform</strong> for people of
              every age. Browse independent counselors, pick who feels right,
              choose a time in <strong>English or Hindi</strong>, pay securely,
              and join a private video session. Book for yourself or on
              someone else's behalf.
            </p>
            <div className="flex flex-wrap gap-3">
              <a data-testid="hero-cta-team" href="#team" className="btn-outline">Meet the counselors</a>
            </div>
            <div className="grid grid-cols-4 gap-6 pt-8 max-w-2xl">
              {[
                {icon: Users, label: "Independent counselors"},
                {icon: GraduationCap, label: "For every age"},
                {icon: Video, label: "Private video"},
                {icon: ShieldCheck, label: "Confidential"},
              ].map(({icon:Icon,label}) => (
                <div key={label} className="flex flex-col gap-2">
                  <Icon className="w-5 h-5" style={{color:"var(--brand)"}}/>
                  <div className="text-xs uppercase tracking-widest" style={{color:"var(--text-secondary)"}}>{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[2rem] overflow-hidden card-soft" style={{ aspectRatio: "4/5" }}>
              <img src={HERO_IMG} alt="Calm space" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl p-4 backdrop-blur-md bg-white/70 border border-white/40">
                <div className="label-eyebrow">Now booking</div>
                <div className="font-serif-display text-2xl">Counselors you can choose from</div>
                <div className="text-sm" style={{color:"var(--text-secondary)"}}>Counseling for every age · Feb 2026</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Counselors */}
      <section id="team" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-7 space-y-3">
            <span className="label-eyebrow">Why {BRAND.shortName}</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl">Counselors who genuinely listen.</h2>
          </div>
          <p className="lg:col-span-5" style={{color:"var(--text-secondary)"}}>
            Every counselor works with people across life stages. Read a
            little about each, then pick who fits — you can switch anytime.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="team-grid">
          {team.map((c, i) => <CounselorCard key={c.id} c={c} idx={i} />)}
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-7 space-y-3">
            <span className="label-eyebrow">Services</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl">Support that fits your real week.</h2>
          </div>
          <p className="lg:col-span-5" style={{color:"var(--text-secondary)"}}>
            One clear conversation at a time — work, relationships, family, self-worth. Book for yourself, or on behalf of someone you care about.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="services-grid">
          {services.map((s, i) => {
            const isHi = !!s.highlight;
            return (
            <div key={s.id} data-testid={`service-card-${s.id}`}
              className="card-soft p-8 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1 relative"
              style={isHi ? {
                borderColor: "var(--brand)",
                boxShadow: "0 20px 40px -24px rgba(91,117,83,0.35)",
                background: "linear-gradient(180deg, rgba(91,117,83,0.05) 0%, #fff 60%)",
              } : {}}>
              {isHi && (
                <span data-testid={`svc-badge-${s.id}`}
                  className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[0.65rem] uppercase tracking-widest font-semibold"
                  style={{background:"var(--accent)", color:"#fff", letterSpacing:"0.18em"}}>
                  {s.badge || "Recommended"}
                </span>
              )}
              <div className="flex items-center justify-between">
                <span className="chip">{s.duration_min} min</span>
                {isHi ? <Sparkles className="w-5 h-5" style={{color:"var(--accent)"}}/>
                      : (i === 0 && <HeartHandshake className="w-5 h-5" style={{color:"var(--accent)"}}/>)}
              </div>
              <div>
                <h3 className="font-serif-display text-2xl leading-tight">{s.name}</h3>
                {s.subtitle && (
                  <div className="text-xs mt-1 font-semibold" style={{color:"var(--brand)"}}>{s.subtitle}</div>
                )}
              </div>
              <p className="text-sm" style={{color:"var(--text-secondary)"}}>{s.description}</p>
              {isHi && followUp && yearlyWeeklyPrice > s.price && (
                <div className="text-xs font-semibold rounded-lg px-3 py-2"
                  style={{background:"rgba(230,178,58,0.12)", color:"var(--accent)"}}>
                  Save ₹{(yearlyWeeklyPrice - s.price).toLocaleString("en-IN")} vs weekly (₹{yearlyWeeklyPrice.toLocaleString("en-IN")}/yr)
                </div>
              )}
              <div className="mt-auto pt-4 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest" style={{color:"var(--text-secondary)"}}>{isHi ? "Full year" : "From"}</div>
                  <div className="font-serif-display text-3xl">₹{Math.round(s.price)}</div>
                </div>
                <Link data-testid={`book-${s.id}`} to="/login"
                  className={isHi ? "btn-primary text-sm" : "btn-outline text-sm"}>Book</Link>
              </div>
            </div>
          );})}
        </div>
      </section>

      {/* About */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="rounded-[2rem] overflow-hidden card-soft">
              <img src={VIBE_IMG} alt="Therapy vibe" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <span className="label-eyebrow">Why {BRAND.shortName}</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">
              A quiet space for everyone,<br />wherever they are.
            </h2>
            <p style={{color:"var(--text-secondary)"}} className="text-lg leading-relaxed max-w-2xl">
              Sessions are held over an encrypted video room — a private link
              is sent as soon as booking is confirmed. You can also book on
              behalf of a family member and join the intake conversation when
              helpful. Each counselor keeps things warm and evidence-informed.
            </p>
            <ul className="grid sm:grid-cols-2 gap-4 pt-4">
              {["Academic pressure & exam stress","Friendships & peer conflict","Family & sibling dynamics","Anxiety, mood & self-worth","Social media & screen habits","Transitions (school, move, loss)"].map(t => (
                <li key={t} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full" style={{background:"var(--accent)"}}/>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">Ready when you are.</h2>
        <p className="mt-4 max-w-xl mx-auto" style={{color:"var(--text-secondary)"}}>
          Pick a counselor, pick a time — book for yourself or someone you care about.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link data-testid="cta-final-book" to="/login" className="btn-primary">Book a session</Link>
        </div>
      </section>
    </div>
  );
}
